import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { ApiError } from '@/lib/api/errors'
import type { ManagedVenueListView } from '@/lib/venue/managerVenueTypes'
import { registerVenue } from '@/lib/venue/venueRegisterApi'
import type { VenueRegisterResult, VenueRegisterSaveInput } from '@/lib/venue/venueRegisterTypes'
import { mergeRegisteredVenue } from '@/lib/venue/venueRegisterViewModel'

import { MANAGED_VENUES_QUERY_KEY } from './useManagedVenues'

/**
 * 무료 구장 등록(api-spec §1, design.md §2 D1).
 *
 * 🚨 성공 시 관리 구장 목록 쿼리를 **재조회(invalidate)하지 않는다** — Reader 복제 지연으로
 * 재조회가 방금 만든 구장을 놓치면 `VenueLayout` 의 소속 검사가 "접근 불가"로 튕긴다. 대신 캐시에
 * 새 구장을 직접 병합한다(`useSaveVenueProfile` 이 프로필 쿼리 자체에 쓰는 "재조회 대신 직접 반영"
 * 패턴을 목록 쿼리에도 적용).
 */
export function useRegisterVenue() {
  const queryClient = useQueryClient()

  return useMutation<VenueRegisterResult, ApiError, VenueRegisterSaveInput>({
    mutationFn: registerVenue,
    onSuccess: (result, input) => {
      queryClient.setQueryData<ManagedVenueListView>(MANAGED_VENUES_QUERY_KEY, (current) =>
        mergeRegisteredVenue(current, result, input)
      )
    },
  })
}
